<template>
  <div v-if="isActive" class="modal-popup" @click="handleOverlayClick">
    <div class="popup two-factor-popup" @click.stop>
      <!-- Success state -->
      <div v-if="connected" class="two-factor-success">
        <div class="success-icon">✓</div>
        <h4>{{ $t('onboarding.2fa.success_title') || 'Connexion réussie !' }}</h4>
        <p v-if="analysts && analysts.length" class="analysts-warning">
          {{ $t('dash.bots.analysts') }} {{ analysts.join(', ') }}
        </p>
        <div class="buttons">
          <div class="button btn-primary" @click="closeSuccess">
            {{ $t('onboarding.2fa.continue') || 'Continuer' }}
          </div>
        </div>
      </div>

      <!-- 2FA form -->
      <div v-else-if="page && twoFactorInfo" class="two-factor-form">
        <!-- Page info -->
        <div class="page-header">
          <div class="page-avatar">
            <img v-if="page.avatar" :src="page.avatar" :alt="page.name" />
            <div v-else class="page-avatar-placeholder">
              {{ page.name ? page.name[0].toUpperCase() : '?' }}
            </div>
          </div>
          <h4>{{ page.name }}</h4>
        </div>

        <!-- Instructions based on verification method -->
        <div class="two-factor-instructions">
          <p v-if="twoFactorInfo.verification_method == 0">
            {{ $t('onboarding.2fa.method_app') || 'Veuillez renseigner ci-dessous le code de vérification reçu sur l\'application vous permettant d\'effectuer vos authentifications à deux facteurs :' }}
          </p>
          <p v-else-if="twoFactorInfo.verification_method == 1">
            {{ $t('onboarding.2fa.method_sms') || 'Veuillez renseigner ci-dessous le code de vérification reçu par SMS :' }}
          </p>
          <p v-else-if="twoFactorInfo.verification_method == 4">
            {{ $t('onboarding.2fa.method_push') || 'Veuillez accepter l\'appareil depuis la notification envoyée par votre application Instagram :' }}
            <br>
            <strong v-if="page.story_config && page.story_config.user_agent">
              {{ page.story_config.user_agent.split(';')[4] }}
            </strong>
          </p>
          <p v-else-if="twoFactorInfo.verification_method == 90">
            {{ $t('onboarding.2fa.method_email_sms') || 'Veuillez entrer le code reçu par Email ou SMS :' }}
            <br>
            <small class="method-note">
              {{ $t('onboarding.2fa.method_experimental') || '(Cette méthode de vérification est en cours de test et peut rencontrer des difficultés)' }}
            </small>
          </p>
          <p v-else-if="twoFactorInfo.verification_method == 99">
            {{ $t('onboarding.2fa.method_push_or_sms') || 'Veuillez accepter l\'appareil depuis la notification Instagram ou entrer le code reçu par SMS :' }}
            <br>
            <small class="method-note">
              {{ $t('onboarding.2fa.method_leave_empty') || '(Si vous recevez une notification, laissez le champ vide)' }}
            </small>
          </p>
          <p v-else-if="twoFactorInfo.verification_method == 98">
            {{ $t('onboarding.2fa.method_linkedin') || 'Veuillez entrer le code reçu par Email ou SMS :' }}
          </p>
        </div>

        <!-- Code input (hidden for push notification only) -->
        <div v-if="twoFactorInfo.verification_method != 4" class="two-factor-input">
          <input
            v-model="verificationCode"
            type="number"
            class="code-input"
            :placeholder="$t('onboarding.2fa.code_placeholder') || 'Code de vérification'"
            @keypress.enter="submitCode"
            autofocus
          />
        </div>

        <!-- Error message -->
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        
        <!-- Info message -->
        <p v-if="infoMessage" class="info-message">{{ infoMessage }}</p>

        <!-- Buttons -->
        <div class="buttons">
          <div class="button btn-outline-primary" @click="cancel">
            {{ $t('onboarding.2fa.cancel') || 'Annuler' }}
          </div>
          <div
            class="button btn-primary"
            :class="{ 'loading': isSubmitting }"
            :disabled="isSubmitDisabled"
            @click="submitCode"
          >
            {{ isSubmitting ? $t('onboarding.2fa.connecting') || 'Connexion...' : $t('onboarding.2fa.connect') || 'Connecter' }}
          </div>
        </div>

        <!-- Resend code (LinkedIn only) -->
        <div v-if="twoFactorInfo.verification_method == 98" class="resend-section">
          <p class="resend-text">{{ $t('dash.pages.linkedin_code') }}</p>
          <button class="resend-button" @click="resendCode">
            {{ $t('dash.pages.linkedin_code_btn') || 'Renvoyer le code' }}
          </button>
        </div>
      </div>

      <!-- Error state -->
      <div v-else class="two-factor-error">
        <h4>{{ $t('onboarding.2fa.error_title') || 'Erreur' }}</h4>
        <p>{{ $t('onboarding.2fa.error_no_page') || 'La page n\'existe pas ou n\'a pas besoin d\'authentification à deux facteurs.' }}</p>
        <div class="buttons">
          <div class="button btn-primary" @click="cancel">
            {{ $t('onboarding.2fa.close') || 'Fermer' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Bots from '../../mixins/Bots.vue'

export default {
  name: 'TwoFactorModal',

  mixins: [Bots],

  props: {
    active: {
      type: Boolean,
      default: false
    },
    page: {
      type: Object,
      default: null
    },
    createPage: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      verificationCode: '',
      errorMessage: '',
      infoMessage: '',
      connected: false,
      analysts: [],
      isSubmitting: false
    }
  },

  computed: {
    isActive() {
      return this.active
    },

    twoFactorInfo() {
      if (!this.page || !this.page.story_config) return null
      
      const { two_factor, checkpoint, challenge_informations, challenge } = this.page.story_config
      
      if (two_factor) {
        try {
          return JSON.parse(two_factor)
        } catch (e) {
          console.error('[TwoFactorModal] Failed to parse two_factor:', e)
          return null
        }
      }
      
      if (checkpoint) {
        return { verification_method: 99 }
      }
      
      if (challenge_informations || challenge) {
        return { verification_method: 98 }
      }
      
      return null
    },

    isSubmitDisabled() {
      if (this.isSubmitting) return true
      
      // Push notification (method 4) doesn't require code
      if (this.twoFactorInfo.verification_method === 4) return false
      
      // LinkedIn challenge (method 98) requires at least 1 char
      if (this.twoFactorInfo.verification_method === 98) {
        return !this.verificationCode || this.verificationCode.length === 0
      }
      
      // Other methods require 6-digit code
      return !this.verificationCode || this.verificationCode.length < 6
    }
  },

  watch: {
    active(newVal) {
      if (newVal) {
        // Reset state when modal opens
        this.verificationCode = ''
        this.errorMessage = ''
        this.infoMessage = ''
        this.connected = false
        this.analysts = []
      }
    }
  },

  methods: {
    handleOverlayClick() {
      // Only allow closing if not submitting
      if (!this.isSubmitting) {
        this.cancel()
      }
    },

    cancel() {
      if (this.isSubmitting) return
      
      this.$emit('cancel')
      this.$emit('update:active', false)
    },

    closeSuccess() {
      this.$emit('success')
      this.$emit('update:active', false)
    },

    async submitCode() {
      if (this.isSubmitDisabled) return
      
      this.errorMessage = ''
      this.infoMessage = ''
      this.isSubmitting = true
      
      try {
        const method = this.twoFactorInfo.verification_method
        
        console.log('[TwoFactorModal] Submitting 2FA code:', {
          network: this.page.network,
          method: method,
          pageId: this.page.id,
          accountId: this.page.account,
          codeLength: this.verificationCode.length
        })
        
        // Determine which API to call based on verification method and network
        let response
        
        if (this.page.network === 'instagram' || [4, 99].includes(method)) {
          // Instagram 2FA
          // IMPORTANT: Despite the method name "twoFactorLinkedinConnect", this is the CORRECT method
          // to use for Instagram 2FA. The method calls /bots/challenge/directapi which handles both
          // Instagram and LinkedIn 2FA. The old "twoFactorInstagramConnect" method pointing to
          // /administration/proxies/connectapi is deprecated and no longer maintained by the backend.
          // See Dashboard's TwoFactorAuthenticationPopup.vue for reference.
          console.log('[TwoFactorModal] Calling twoFactorLinkedinConnect for Instagram 2FA...')
          response = await this.twoFactorLinkedinConnect({
            verificationCode: this.verificationCode,
            pageId: this.page.id,
            createPage: this.createPage
          })
          
          console.log('[TwoFactorModal] Instagram 2FA response:', {
            ok: response.ok,
            status: response.status,
            body: response.body,
            bodyConnected: response.body?.connected
          })
          
          if (response.ok && response.body && response.body.connected) {
            console.log('[TwoFactorModal] Instagram 2FA success!')
            this.connected = true
            this.analysts = response.body.analysts || []
            
            // Emit success after short delay
            setTimeout(() => {
              this.$emit('connected')
            }, 1500)
          } else if (response.ok && response.body && response.body.badPin) {
            console.error('[TwoFactorModal] Instagram 2FA failed - bad PIN')
            this.errorMessage = this.$t('dash.pages.code_enter_incorrect') || 'Code incorrect'
          } else {
            console.error('[TwoFactorModal] Instagram 2FA failed - response:', response.body)
            this.errorMessage = `${response.status}: ${response.body?.[0]?.msg || response.bodyText || 'Erreur de connexion'}`
          }
        } else {
          // LinkedIn 2FA or other networks
          response = await this.twoFactorLinkedinConnect({
            verificationCode: this.verificationCode,
            pageId: this.page.id,
            createPage: this.createPage
          })
          
          if (response.ok && response.body && response.body.connected) {
            this.connected = true
            this.analysts = response.body.analysts || []
            
            // Emit success after short delay
            setTimeout(() => {
              this.$emit('connected')
            }, 1500)
          } else if (response.ok && response.body && response.body.badPin) {
            this.errorMessage = this.$t('dash.pages.code_enter_incorrect') || 'Code incorrect'
          } else {
            this.errorMessage = `${response.status}: ${response.body?.[0]?.msg || response.bodyText || 'Erreur de connexion'}`
          }
        }
      } catch (error) {
        console.error('[TwoFactorModal] Error submitting code:', error)
        this.errorMessage = this.$t('onboarding.2fa.error_network') || 'Erreur réseau'
      } finally {
        this.isSubmitting = false
      }
    },

    async resendCode() {
      this.errorMessage = ''
      this.infoMessage = ''
      
      try {
        const response = await this.twoFactorLinkedinResendCode({
          pageId: this.page.id
        })
        
        if (response.ok && response.body && response.body.success && response.body.resend) {
          this.infoMessage = this.$t('dash.pages.linkedin_code_success') || 'Code renvoyé avec succès'
        } else {
          this.errorMessage = `${response.status}: ${response.body?.[0]?.msg || response.bodyText || 'Erreur lors du renvoi'}`
        }
      } catch (error) {
        console.error('[TwoFactorModal] Error resending code:', error)
        this.errorMessage = this.$t('onboarding.2fa.error_resend') || 'Impossible de renvoyer le code'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../variables.scss';

// Use global .modal-popup and .popup styles
// Only define 2FA-specific styles here

.two-factor-popup {
  max-width: 480px;
}

.two-factor-success {
  text-align: center;
  padding: 20px 0;

  .success-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 20px;
    background: #10b981;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 32px;
    font-weight: bold;
  }

  h4 {
    color: #111827;
    margin-bottom: 12px;
  }

  .analysts-warning {
    color: #f59e0b;
    font-size: 14px;
    margin-top: 16px;
    margin-bottom: 0;
  }
}

.two-factor-form {
  .page-header {
    text-align: center;
    margin-bottom: 24px;

    .page-avatar {
      width: 56px;
      height: 56px;
      margin: 0 auto 12px;
      border-radius: 50%;
      overflow: hidden;
      background: $bg-ui-selected;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &-placeholder {
        font-size: 24px;
        font-weight: 700;
        color: $primary-color;
      }
    }

    h4 {
      margin: 0;
      color: #111827;
      font-size: 18px;
    }
  }

  .two-factor-instructions {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
      color: #374151;
    }

    strong {
      color: #111827;
      font-weight: 600;
    }

    .method-note {
      display: block;
      margin-top: 8px;
      font-size: 12px;
      color: #6b7280;
      font-style: italic;
    }
  }

  .two-factor-input {
    margin-bottom: 16px;

    .code-input {
      width: 100%;
      padding: 12px 16px;
      font-size: 16px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      text-align: center;
      letter-spacing: 4px;
      font-weight: 500;
      transition: all 0.2s ease;

      &:focus {
        outline: none;
        border-color: $primary-color;
        box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
      }

      &::placeholder {
        letter-spacing: normal;
        color: #9ca3af;
      }

      // Remove arrows from number input
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &[type=number] {
        -moz-appearance: textfield;
      }
    }
  }

  .error-message {
    background: #fee2e2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    text-align: center;
  }

  .info-message {
    background: #dbeafe;
    border: 1px solid #bfdbfe;
    color: #1e40af;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    text-align: center;
  }

  .resend-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
    text-align: center;

    .resend-text {
      font-size: 13px;
      color: #6b7280;
      margin-bottom: 8px;
    }

    .resend-button {
      background: transparent;
      border: none;
      color: $primary-color;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      padding: 8px 16px;
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        background: $bg-ui-selected;
      }
    }
  }
}

.two-factor-error {
  text-align: center;
  padding: 20px 0;

  h4 {
    color: #dc2626;
    margin-bottom: 12px;
  }

  p {
    color: #6b7280;
    font-size: 14px;
  }
}

.button {
  &.loading {
    opacity: 0.7;
    cursor: not-allowed;
  }
}
</style>
